import { useEffect } from 'react';
import { Card } from '../ui/Card.jsx';
import { Button } from '../ui/Button.jsx';
import { Dice } from '../ui/Dice.jsx';
import { useAudio } from '../../hooks/useAudio.js';
import { TASK_DEFINITIONS, getTask, getTaskSoundEffect, isOutdoorTask } from '../../config/tasks.js';
import { calculateTaskTotal } from '../../systems/taskSystem.js';

export function ResultsCard({ rolls, pushes, event, buildings, bountyTask, onPush, onSelectBounty, onEndDay }) {
  const audio = useAudio();

  // Play task sounds and staggered dice sounds when results appear
  useEffect(() => {
    if (rolls) {
      const playTaskSounds = async () => {
        // Play task sounds first, staggered
        for (const [task, rollData] of Object.entries(rolls)) {
          const soundEffect = getTaskSoundEffect(task);
          if (soundEffect && rollData.rolls.length > 0) {
            audio.playSound(soundEffect);
            // Small delay between task sounds (150ms)
            await new Promise(resolve => setTimeout(resolve, 150));
          }
        }

        // Then play all dice sounds staggered
        const totalDice = Object.values(rolls).reduce(
          (sum, rollData) => sum + rollData.rolls.length,
          0
        );
        audio.playDice('d6', totalDice);
      };

      playTaskSounds();
    }
  }, [rolls]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate task names dynamically from task definitions
  const taskNames = Object.fromEntries(
    Object.values(TASK_DEFINITIONS).map(task => [task.id, task.label])
  );

  const isStorm = event?.type === 'storm';
  const isBounty = event?.type === 'bounty';

  // Filter out tasks that don't show in results (rest, trade handled separately)
  const taskResults = Object.entries(rolls).filter(([task]) => {
    const taskDef = TASK_DEFINITIONS[task];
    return task !== 'rest' && taskDef?.specialMechanic !== 'trade_dialog';
  });

  // Use centralized task total calculation
  const getTaskTotal = (task, rollData) => {
    return calculateTaskTotal({
      task,
      rollData,
      pushes,
      buildings,
      event,
      bountyTask
    });
  };

  return (
    <Card title="Results" icon="🎲">
      <div className="space-y-4 mb-6" role="list" aria-label="Task results">
        {taskResults.map(([task, rollData]) => {
          const total = getTaskTotal(task, rollData);
          const canPush = !pushes[task] && rollData.rolls.length > 0;
          const pushResult = pushes[task];
          const taskIsOutdoor = isOutdoorTask(task);
          const taskDef = getTask(task);
          const divisor = taskDef?.outputDivisor || 1;
          const actualGain = Math.floor(total / divisor);

          // Format display: show divisor info if divisor > 1
          let displayTotal;
          if (isStorm && taskIsOutdoor) {
            displayTotal = '⛈️ 0';
          } else if (divisor > 1) {
            displayTotal = `${total} (÷${divisor} → ${actualGain})`;
          } else {
            displayTotal = total;
          }

          return (
            <div key={task} className="bg-amber-100 p-3 rounded-lg" role="listitem">
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-amber-900">{taskNames[task]}</span>
                <span className="text-lg font-bold text-amber-800">
                  {displayTotal}
                </span>
              </div>
              <div className="flex gap-2 mb-2 flex-wrap">
                {rollData.rolls.map((val, i) => (
                  <Dice key={i} value={val} size="sm" />
                ))}
                {pushResult && (
                  <div className={`flex items-center gap-1 ${pushResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    <span>→</span>
                    <Dice value={pushResult.value} size="sm" />
                    {!pushResult.success && <span className="text-sm">💥 Injured!</span>}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {canPush && (
                  <Button
                    variant="secondary"
                    onClick={() => onPush(task)}
                    className="text-sm py-1 px-3"
                  >
                    Push (risk injury)
                  </Button>
                )}
                {isBounty && !bountyTask && (
                  <Button
                    variant="secondary"
                    onClick={() => onSelectBounty(task)}
                    className="text-sm py-1 px-3"
                  >
                    ✨ Double this!
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-center">
        <Button onClick={onEndDay}>End Day</Button>
      </div>
    </Card>
  );
}
